import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react'
import api from '@services/enhancedApiService'

const ThemeContext = createContext()

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

const resolveTheme = (preference) => {
  if (preference === 'system' || !preference) {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return preference
}

export const ThemeProvider = ({ children }) => {
  const serverLoaded = useRef(false)
  const saveTimer = useRef(null)

  // preference = 'light' | 'dark' | 'system'
  const [preference, setPreference] = useState(() => {
    return localStorage.getItem('theme') || 'system'
  })

  const [colorBlindMode, setColorBlindModeState] = useState(() => {
    return localStorage.getItem('colorBlindMode') === 'true'
  })

  const theme = resolveTheme(preference)

  // Load from server on mount
  useEffect(() => {
    api.get('/user/theme-preference')
      .then(res => {
        const data = res.data?.data
        if (data?.theme) {
          setPreference(data.theme)
          localStorage.setItem('theme', data.theme)
        }
        if (data?.colorBlindMode !== undefined) {
          setColorBlindModeState(data.colorBlindMode)
          localStorage.setItem('colorBlindMode', String(data.colorBlindMode))
        }
      })
      .catch(() => {})
      .finally(() => { serverLoaded.current = true })
  }, [])

  // Save to server (debounced)
  const saveToServer = useCallback((payload) => {
    if (!serverLoaded.current) return
    clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => {
      api.put('/user/theme-preference', payload).catch(() => {})
    }, 1000)
  }, [])

  const updatePreference = useCallback((pref) => {
    setPreference(pref)
    localStorage.setItem('theme', pref)
    saveToServer({ theme: pref })
  }, [saveToServer])

  const setColorBlindMode = useCallback((enabled) => {
    setColorBlindModeState(enabled)
    localStorage.setItem('colorBlindMode', String(enabled))
    saveToServer({ colorBlindMode: enabled })
  }, [saveToServer])

  const toggleTheme = () => {
    updatePreference(theme === 'light' ? 'dark' : 'light')
  }

  const setLightTheme = () => updatePreference('light')
  const setDarkTheme = () => updatePreference('dark')

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement
    root.classList.remove('light', 'dark')
    root.classList.add(theme)
    root.classList.toggle('color-blind', colorBlindMode)
  }, [theme, colorBlindMode])

  // Listen for system theme changes (only when preference is 'system')
  useEffect(() => {
    if (preference !== 'system') return
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => setPreference(prev => prev === 'system' ? 'system' : prev)
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [preference])

  const value = {
    theme,
    preference,
    toggleTheme,
    setLightTheme,
    setDarkTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    colorBlindMode,
    setColorBlindMode
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}
